// CodingReviewDetail.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Grid, GridItem, Heading, Text, Button, Spinner,
  Flex, Badge, Divider, useToast, IconButton, Textarea,
  Table, Thead, Tbody, Tr, Th, Td, Input, FormControl,
  FormLabel, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
  Select, NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay
} from "@chakra-ui/react";
import { 
  CheckIcon, EditIcon, AddIcon, DeleteIcon, 
  DownloadIcon, ViewIcon, ArrowBackIcon 
} from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import ReactMarkdown from 'react-markdown';

function CodingReviewDetail() {
  //const { caseId } = useParams();

  const { caseId: routeParamCaseId } = useParams();
  const [searchParams] = useSearchParams();
  const queryParamCaseId = searchParams.get('caseId');
  
  // Use either the route param or query param, with route param taking precedence
  const caseId = routeParamCaseId || queryParamCaseId;


  const [isLoading, setIsLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [codingResult, setCodingResult] = useState(null);
  const [codingHistory, setCodingHistory] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [currentFileName, setCurrentFileName] = useState('');
  
  // Update your handleFileClick function
  const handleFileClick = async (fileType) => {
    if (!caseData || !caseData.external_patient_id) {
      toast({
        title: 'Error',
        description: 'Patient ID not available.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      const response = await callApi('/getfile', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          external_patient_id: caseData.external_patient_id, 
          file_type: fileType 
        }),
      });
  
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Set the PDF URL and open the modal
        setPdfUrl(url);
        setCurrentFileName(fileType);
        setIsPdfModalOpen(true);
        
        // Clean up the URL object when modal is closed
        // This will be handled in the onClose function of the modal
      } else {
        throw new Error('Error downloading file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Add a function to handle modal close and cleanup
  const handlePdfModalClose = () => {
    setIsPdfModalOpen(false);
    // Clean up the object URL to prevent memory leaks
    if (pdfUrl) {
      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }, 100);
    }
  };
  
  

  // Modal controls
  const { 
    isOpen: isEditModalOpen, 
    onOpen: onEditModalOpen, 
    onClose: onEditModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isTagModalOpen, 
    onOpen: onTagModalOpen, 
    onClose: onTagModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isDeleteAlertOpen, 
    onOpen: onDeleteAlertOpen, 
    onClose: onDeleteAlertClose 
  } = useDisclosure();
  
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [tagNotes, setTagNotes] = useState('');

  useEffect(() => {
    if (caseId) {
      loadCaseData();
      loadCodingResult();
      loadCodingHistory();
    }
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      const response = await callApi(`/getcase`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch case data');
      }

      const data = await response.json();
      setCaseData(data);
      
      if (data.case_files) {
        setCaseFiles(data.case_files);
      }
    } catch (error) {
      console.error('Error loading case data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load case data.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };


  const loadCodingResult = async () => {
    setIsLoading(true);
    try {
      const response = await callApi(`/coding/results/${caseId}`, accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setCodingResult(null);
          return;
        }
        throw new Error('Failed to fetch coding result');
      }

      const data = await response.json();
      setCodingResult(data);
    } catch (error) {
      console.error('Error loading coding result:', error);
      toast({
        title: 'Error',
        description: 'Failed to load coding result.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCodingHistory = async () => {
    try {
      const response = await callApi(`/coding/results/${caseId}/history`, accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setCodingHistory([]);
          return;
        }
        throw new Error('Failed to fetch coding history');
      }

      const data = await response.json();
      setCodingHistory(data);
    } catch (error) {
      console.error('Error loading coding history:', error);
    }
  };

  const handleFileClickOrig = async (fileType) => {
    if (!caseData || !caseData.external_patient_id) {
      toast({
        title: 'Error',
        description: 'Patient ID not available.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      const response = await callApi('/getfile', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          external_patient_id: caseData.external_patient_id, 
          file_type: fileType 
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          toast({
            title: 'Warning',
            description: 'Please allow pop-ups for this website.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        throw new Error('Error downloading file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      const response = await callApi(`/coding/results/${caseId}/approve`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: reviewNotes }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve coding result');
      }

      toast({
        title: 'Success',
        description: 'Coding result approved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh data
      loadCodingResult();
      loadCodingHistory();
      setReviewNotes('');
    } catch (error) {
      console.error('Error approving coding result:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve coding result.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCode = (code) => {
    setEditingCode({
      ...codingResult.cpt_codes[code],
      code: code
    });
    onEditModalOpen();
  };

  const handleSaveEdit = async () => {
    if (!editingCode) return;
    
    setIsSubmitting(true);
    try {
      // Create a copy of the current CPT codes
      const updatedCptCodes = { ...codingResult.cpt_codes };
      
      // Update the specific code
      updatedCptCodes[editingCode.code] = {
        cpt_code: editingCode.cpt_code,
        cpt_description: editingCode.cpt_description,
        units: editingCode.units,
        reason: editingCode.reason,
        result: true,
        child_modalities: editingCode.child_modalities || []
      };
      
      const response = await callApi(`/coding/results/${caseId}/update`, accessToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          cpt_codes: updatedCptCodes,
          notes: `Updated CPT code: ${editingCode.code}`
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update coding result');
      }

      toast({
        title: 'Success',
        description: 'CPT code updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh data
      loadCodingResult();
      loadCodingHistory();
      onEditModalClose();
    } catch (error) {
      console.error('Error updating coding result:', error);
      toast({
        title: 'Error',
        description: 'Failed to update CPT code.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const promptDeleteCode = (code) => {
    setCodeToDelete(code);
    onDeleteAlertOpen();
  };
  
  const handleDeleteCode = async () => {
    if (!codeToDelete) return;
    
    setIsSubmitting(true);
    try {
      // Create a copy of the current CPT codes
      const updatedCptCodes = { ...codingResult.cpt_codes };
      
      // Remove the specific code
      delete updatedCptCodes[codeToDelete];
      
      const response = await callApi(`/coding/results/${caseId}/update`, accessToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          cpt_codes: updatedCptCodes,
          notes: `Removed CPT code: ${codeToDelete}`
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update coding result');
      }

      toast({
        title: 'Success',
        description: 'CPT code removed successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh data
      loadCodingResult();
      loadCodingHistory();
      onDeleteAlertClose();
    } catch (error) {
      console.error('Error updating coding result:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove CPT code.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTagUser = async () => {
    if (!selectedUser) {
      toast({
        title: 'Warning',
        description: 'Please select a user to tag.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await callApi(`/coding/results/${codingResult.id}/tag`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: selectedUser,
          notes: tagNotes
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to tag user');
      }

      toast({
        title: 'Success',
        description: 'User tagged successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setSelectedUser('');
      setTagNotes('');
      onTagModalClose();
    } catch (error) {
      console.error('Error tagging user:', error);
      toast({
        title: 'Error',
        description: 'Failed to tag user.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await callApi('/get_user_list', accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      if (data && data.user_list && Array.isArray(data.user_list)) {
        setUsers(data.user_list);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Load users when tag modal is opened
  useEffect(() => {
    if (isTagModalOpen) {
      loadUsers();
    }
  }, [isTagModalOpen]);

  if (isLoading) {
    return (
      <Box textAlign="center" fontSize="xl">
        <HeaderMenus />
        <Flex justify="center" align="center" height="80vh">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box textAlign="center" fontSize="xl">
      <HeaderMenus />
      
      <Box maxWidth="1200px" margin="0 auto" p={5}>
        <Flex justify="space-between" align="center" mb={6}>
          <Button 
            leftIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/coding-review')}
            size="sm"
          >
            Back to List
          </Button>
          
          <Heading as="h1" size="lg">
            Coding Review
          </Heading>
          
          <Box>
            <Badge 
              colorScheme={
                codingResult?.status === 'approved' ? 'green' : 
                codingResult?.status === 'needs_review' ? 'orange' : 'gray'
              }
              fontSize="md"
              p={2}
            >
              {codingResult?.status || 'No Status'}
            </Badge>
          </Box>
        </Flex>
        {/* Current Coding Notes */}
{codingResult && (
  <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={6}>
    <Flex justify="space-between" align="center" mb={4}>
      <Heading as="h2" size="md" textAlign="left">
        Current Coding Notes
      </Heading>
      
      <Badge 
        colorScheme={
          codingResult.status === 'approved' ? 'green' : 
          codingResult.status === 'needs_review' ? 'orange' : 'gray'
        }
        fontSize="sm"
        p={1}
      >
        {codingResult.status}
      </Badge>
    </Flex>
    
    {codingResult.notes ? (
      <Box 
        bg="gray.50" 
        p={4} 
        borderRadius="md" 
        borderLeft="4px solid" 
        borderColor="blue.500"
        textAlign="left"
      >
    <ReactMarkdown>{codingResult.notes}</ReactMarkdown>
    </Box>
    ) : (
      <Text textAlign="left" fontStyle="italic" color="gray.500">
        No notes provided for this coding result.
      </Text>
    )}
    
    <Flex justify="space-between" mt={3}>
      <Text fontSize="sm" color="gray.500" textAlign="left">
        Created by: {codingResult.created_by}
      </Text>
      <Text fontSize="sm" color="gray.500">
        {new Date(codingResult.created_at).toLocaleString()}
      </Text>
    </Flex>
  </Box>
)}
        {/* Case Information */}
        <Grid templateColumns="repeat(12, 1fr)" gap={6} mb={6}>
          <GridItem colSpan={{ base: 12, md: 8 }}>
            <Box bg="white" p={5} borderRadius="md" boxShadow="sm">
              <Heading as="h2" size="md" mb={4} textAlign="left">
                Case Information
              </Heading>
              
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box textAlign="left">
                  <Text fontWeight="bold">Case ID:</Text>
                  <Text>{caseId}</Text>
                </Box>
                
                {caseData && (
                  <>
                    <Box textAlign="left">
                      <Text fontWeight="bold">Patient ID:</Text>
                      <Text>{caseData.external_patient_id || 'N/A'}</Text>
                    </Box>
                    
                    <Box textAlign="left">
                      <Text fontWeight="bold">Patient Name:</Text>
                      <Text>
                        {caseData.patient_first_name || ''} {caseData.patient_last_name || ''}
                      </Text>
                    </Box>
                    
                    <Box textAlign="left">
                      <Text fontWeight="bold">Procedure Date:</Text>
                      <Text>{caseData.procedure_date || 'N/A'}</Text>
                    </Box>
                    
                    <Box textAlign="left">
                      <Text fontWeight="bold">Case Type:</Text>
                      <Text>{caseData.case_name || codingResult?.case_type || 'N/A'}</Text>
                    </Box>
                    
                    <Box textAlign="left">
                      <Text fontWeight="bold">Payer:</Text>
                      <Text>{caseData.payer || 'N/A'}</Text>
                    </Box>
                  </>
                )}
              </Grid>
            </Box>
          </GridItem>
          
          <GridItem colSpan={{ base: 12, md: 4 }}>
            <Box bg="white" p={5} borderRadius="md" boxShadow="sm" height="100%">
              <Heading as="h2" size="md" mb={4} textAlign="left">
                Case Files
              </Heading>
              
              {caseFiles && caseFiles.length > 0 ? (
                <Box>
                  {caseFiles.map((file, index) => (
                    <Flex 
                      key={index} 
                      justify="space-between" 
                      align="center" 
                      mb={2} 
                      p={2} 
                      borderRadius="md"
                      _hover={{ bg: "gray.50" }}
                    >
                      <Text fontSize="sm">{file}</Text>
                      <IconButton
                        aria-label="View file"
                        icon={<ViewIcon />}
                        size="sm"
                        onClick={() => handleFileClick(file)}
                      />
                    </Flex>
                  ))}
                </Box>
              ) : (
                <Text fontSize="sm">No files available</Text>
              )}
            </Box>
          </GridItem>
        </Grid>
        
        {/* CPT Codes */}
        <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading as="h2" size="md" textAlign="left">
              CPT Codes
            </Heading>
            
            <Flex>
              <Button 
                leftIcon={<AddIcon />} 
                colorScheme="blue" 
                size="sm" 
                mr={2}
                onClick={() => {
                  setEditingCode({
                    code: '',
                    cpt_code: '',
                    cpt_description: '',
                    units: 1,
                    reason: '',
                    child_modalities: []
                  });
                  onEditModalOpen();
                }}
              >
                Add Code
              </Button>
              
              <Button 
                leftIcon={<CheckIcon />} 
                colorScheme="green" 
                size="sm"
                isDisabled={codingResult?.status === 'approved'}
                onClick={handleApprove}
                isLoading={isSubmitting}
              >
                Approve
              </Button>
            </Flex>
          </Flex>
          
          {codingResult && codingResult.cpt_codes ? (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>CPT Code</Th>
                  <Th>Description</Th>
                  <Th>Units</Th>
                  <Th>Reason</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(codingResult.cpt_codes).map(([code, details]) => (
                  <Tr key={code}>
                    <Td>{details.cpt_code}</Td>
                    <Td fontSize="sm"  maxW="100px"whiteSpace="wrap" >{details.cpt_description}</Td>
                    <Td>{details.units || 1}</Td>
                    <Td fontSize="sm" maxW="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="wrap">
                      {details.reason}
                    </Td>
                    <Td>
                      <Flex>
                        <IconButton
                          aria-label="Edit code"
                          icon={<EditIcon />}
                          size="sm"
                          mr={2}
                          onClick={() => handleEditCode(code)}
                          isDisabled={codingResult.status === 'approved'}
                        />
                        <IconButton
                          aria-label="Delete code"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => promptDeleteCode(code)}
                          isDisabled={codingResult.status === 'approved'}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Text>No CPT codes available for this case.</Text>
          )}
          
          {/* Review Notes */}
          {codingResult?.status !== 'approved' && (
            <Box mt={6}>
              <FormControl>
                <FormLabel>Review Notes</FormLabel>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this review (optional)"
                />
              </FormControl>
              
              <Flex justify="space-between" mt={4}>
                <Button 
                  leftIcon={<CheckIcon />} 
                  colorScheme="green"
                  onClick={handleApprove}
                  isLoading={isSubmitting}
                >
                  Approve Coding
                </Button>
                
                <Button 
                  onClick={onTagModalOpen}
                  variant="outline"
                >
                  Tag User for Review
                </Button>
              </Flex>
            </Box>
          )}
        </Box>
        
        {/* Coding History */}
        <Box bg="white" p={5} borderRadius="md" boxShadow="sm">
          <Heading as="h2" size="md" mb={4} textAlign="left">
            Coding History
          </Heading>
          
          {codingHistory.length > 0 ? (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th>Created By</Th>
                  <Th>Notes</Th>
                </Tr>
              </Thead>
              <Tbody>
                {codingHistory.map((history) => (
                  <Tr key={history.id}>
                    <Td>{new Date(history.created_at).toLocaleString()}</Td>
                    <Td>
                      <Badge 
                        colorScheme={
                          history.status === 'approved' ? 'green' : 
                          history.status === 'needs_review' ? 'orange' : 'gray'
                        }
                      >
                        {history.status}
                      </Badge>
                    </Td>
                    <Td>{history.created_by}</Td>
                    <Td>{history.notes || 'No notes'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Text>No coding history available.</Text>
          )}
        </Box>
      </Box>
      
      {/* Edit CPT Code Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCode?.code ? `Edit CPT Code: ${editingCode.code}` : 'Add New CPT Code'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>CPT Code</FormLabel>
              <Input 
                value={editingCode?.cpt_code || ''}
                onChange={(e) => setEditingCode({...editingCode, cpt_code: e.target.value})}
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Input 
                value={editingCode?.cpt_description || ''}
                onChange={(e) => setEditingCode({...editingCode, cpt_description: e.target.value})}
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Units</FormLabel>
              <NumberInput 
                min={1} 
                value={editingCode?.units || 1}
                onChange={(valueString) => setEditingCode({...editingCode, units: parseInt(valueString)})}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Reason</FormLabel>
              <Textarea 
                value={editingCode?.reason || ''}
                onChange={(e) => setEditingCode({...editingCode, reason: e.target.value})}
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSaveEdit}
              isLoading={isSubmitting}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Tag User Modal */}
      <Modal isOpen={isTagModalOpen} onClose={onTagModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Tag User for Review

</ModalHeader>
<ModalCloseButton />
<ModalBody>
  <FormControl mb={4}>
    <FormLabel>Select User</FormLabel>
    <Select 
      placeholder="Select user"
      value={selectedUser}
      onChange={(e) => setSelectedUser(e.target.value)}
    >
      {users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name} ({user.email})
        </option>
      ))}
    </Select>
  </FormControl>
  
  <FormControl mb={4}>
    <FormLabel>Notes (Optional)</FormLabel>
    <Textarea 
      value={tagNotes}
      onChange={(e) => setTagNotes(e.target.value)}
      placeholder="Add notes for the tagged user"
      rows={4}
    />
  </FormControl>
</ModalBody>
<ModalFooter>
  <Button variant="ghost" mr={3} onClick={onTagModalClose}>
    Cancel
  </Button>
  <Button 
    colorScheme="blue" 
    onClick={handleTagUser}
    isLoading={isSubmitting}
    isDisabled={!selectedUser}
  >
    Tag User
  </Button>
</ModalFooter>
</ModalContent>
</Modal>

{/* Delete Confirmation Alert */}
<AlertDialog
isOpen={isDeleteAlertOpen}
leastDestructiveRef={cancelRef}
onClose={onDeleteAlertClose}
>
<AlertDialogOverlay>
<AlertDialogContent>
  <AlertDialogHeader fontSize="lg" fontWeight="bold">
    Delete CPT Code
  </AlertDialogHeader>

  <AlertDialogBody>
    Are you sure you want to delete CPT code {codeToDelete}? This action cannot be undone.
  </AlertDialogBody>

  <AlertDialogFooter>
    <Button ref={cancelRef} onClick={onDeleteAlertClose}>
      Cancel
    </Button>
    <Button 
      colorScheme="red" 
      onClick={handleDeleteCode} 
      ml={3}
      isLoading={isSubmitting}
    >
      Delete
    </Button>
  </AlertDialogFooter>
</AlertDialogContent>
</AlertDialogOverlay>
</AlertDialog>
<Modal 
  isOpen={isPdfModalOpen} 
  onClose={handlePdfModalClose}
  size="full"
  motionPreset="slideInBottom"
>
  <ModalOverlay />
  <ModalContent height="90vh">
    <ModalHeader>
      <Flex justify="space-between" align="center">
        <Text>{currentFileName}</Text>
        <Flex>
          <IconButton
            icon={<DownloadIcon />}
            aria-label="Download PDF"
            mr={2}
            onClick={() => {
              if (pdfUrl) {
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.download = currentFileName || 'document.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }
            }}
          />
          <IconButton
            icon={<ViewIcon />}
            aria-label="Open in new tab"
            mr={2}
            onClick={() => {
              if (pdfUrl) {
                window.open(pdfUrl, '_blank');
              }
            }}
          />
          <ModalCloseButton position="static" />
        </Flex>
      </Flex>
    </ModalHeader>
    <ModalBody p={0}>
      {pdfUrl && (
        <Box height="100%" width="100%">
          <iframe
            src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            title="PDF Viewer"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </Box>
      )}
    </ModalBody>
  </ModalContent>
</Modal>



</Box>
);
}

export default CodingReviewDetail;